import React, { useEffect, useState } from 'react';
import Load from '../../../models/load';
import get from '../../../services/axios/get';
import Loader from '../../UI/Loader/Loader';
import classes from './Tracking.module.scss';
import { FaCheckCircle, FaCircle, FaTimes } from 'react-icons/fa';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { customAlert } from '../../../store/actions/alert';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import config from '../../../config/config.json';

interface TrackingProps {
  load: Load;
  close: () => void;
}

type EventType = 'atLoadPoint' | 'atPort' | 'atEmptyDepot' | 'enRouteToLoadPoint' | 'enRouteToPort' | 'atLoadPoint2' | 'enRouteToLoadPoint2';

interface TrackingEvent {
  id: string;
  eventType: EventType;
  createdAt: string;
  truckReg: string;
}

const eventTypeToLabel: Record<EventType, string> = {
  atEmptyDepot: 'At Empty Depot',
  enRouteToLoadPoint: 'En-route to Load Point',
  atLoadPoint: 'At Load Point 1',
  atLoadPoint2: 'At Load Point 2',
  enRouteToLoadPoint2: 'En-route to Load Point 2',
  enRouteToPort: 'En-route to Port',
  atPort: 'At Port',
};

const Tracking: React.FC<TrackingProps> = ({ load, close }) => {
  const [events, setEvents] = useState<Record<string, TrackingEvent>>({});
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [isMapModalOpen, setMapModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: config.googleMapsApiKey,
  });
  const user = useAppSelector(state => state.auth.user);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!load.driver || typeof load.driver !== 'object' || !load.truckReg) {
      setError('No truck registration found for this load.');
      setIsLoading(false);
      return;
    }

    const doFetchCoordinates = () =>
      get(
        `/tracking`,
        { truckReg: load.truckReg, loadId: load.id },
        (res) => {
          const { lat, lng } = res.data;
          if (lat && lng) {
            setCoordinates({ lat: parseFloat(lat), lng: parseFloat(lng) });
          }
        },
        (err) => {
          console.error('Failed to fetch coordinates', err);
        },
      );

    setIsLoading(true);
    const fetchEvents = get(
      `/events/latest`,
      { truckReg: load.truckReg, loadId: load.id },
      (res) => setEvents(res.data),
      (err) => {
        dispatch(customAlert(true, err || 'Failed to fetch tracking events.'));
        setError(err || 'Failed to fetch tracking events.');
      },
    );

    const fetchInitialCoordinates = doFetchCoordinates();

    Promise.all([fetchEvents, fetchInitialCoordinates]).finally(() => {
      setIsLoading(false);
    });

    const intervalId = setInterval(doFetchCoordinates, 60000);

    return () => clearInterval(intervalId);
  }, [load, dispatch]);

  const trackingSteps: EventType[] = [
    'atEmptyDepot',
    'enRouteToLoadPoint',
    'atLoadPoint',
  ];

  if (load.loadPoint2 != null) {
    trackingSteps.push('enRouteToLoadPoint2');
    trackingSteps.push('atLoadPoint2');
  }

  trackingSteps.push('enRouteToPort', 'atPort');

  const renderSteps = () => {
    if (isLoading) {
      return <div className={classes.loaderContainer}><Loader /></div>;
    }

    if (error) {
      return <div className={classes.error}>{error}</div>;
    }

    if (Object.keys(events).length === 0) {
      return <div className={classes.noEvents}>No tracking information available yet.</div>;
    }

    return (
      <div className={classes.trackingSteps}>
        {trackingSteps.map((step, index) => {
          const isCompleted = events[step] !== undefined;
          const event = events[step];

          return (
            <div key={step} className={`${classes.step} ${isCompleted ? classes.completed : ''}`}>
              <div className={classes.iconContainer}>
                {isCompleted ? <FaCheckCircle className={classes.icon} /> : <FaCircle className={classes.icon} />}
                {index < trackingSteps.length - 1 && <div className={classes.line}></div>}
              </div>
              <div className={classes.details}>
                <div className={classes.label}>{eventTypeToLabel[step]}</div>
                {isCompleted && event && (
                  <div className={classes.timestamp}>{new Date(event.createdAt).toLocaleString('en-GB', { timeZone: 'Africa/Johannesburg' })}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMap = () =>
    isLoaded &&
    coordinates && (
      <GoogleMap
        mapContainerStyle={{ height: '100%', width: '100%' }}
        center={coordinates}
        zoom={11}
        options={{
          mapId: 'tracking-map',
          disableDefaultUI: true,
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: true,
          fullscreenControl: true,
        }}
      >
        <Marker position={coordinates} />
      </GoogleMap>
    );

  return (
    <div className={classes.trackingContainer}>
      <div className={classes.header}>
        <h4>Tracking for Load: {load.clientRef || load.bookingRef}</h4>
      </div>
      <div className={classes.content}>
        {user?.userType === 'admin' ? renderSteps() : null}

        {isLoaded && coordinates && (
          <>
            {isMobile ? (
              <div className={classes.mapButtonContainer}>
                <button onClick={() => setMapModalOpen(true)} className={classes.mapButton}>
                  View on Map
                </button>
              </div>
            ) : (
              <div className={classes.mapContainer}>{renderMap()}</div>
            )}
          </>
        )}
      </div>
      {isMapModalOpen && (
        <div className={classes.mapModal}>
          <div className={classes.mapModalContent}>
            <button onClick={() => setMapModalOpen(false)} className={classes.closeButton}>
              <FaTimes />
            </button>
            {renderMap()}
          </div>
        </div>
      )}
    </div>
  );
};

export default Tracking;
